import { useMemo } from 'react';
import { formatShortDate } from '@shared/time.ts';
import type { ExtractedSegment } from '@shared/ingest/extracted.ts';
import { getStore, useAppState, useSegments } from '../../data/store.ts';
import { Icon } from '../../components/Icon.tsx';
import { Link, back, navigate } from '../../lib/router.tsx';
import { SegmentForm } from './SegmentForm.tsx';
import { whoIsGoing } from '@shared/people.ts';

export function NewSegmentScreen() {
  const store = getStore();
  const me = useAppState((s) => s.me);
  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/trips')}>
        <Icon name="chevron-left" size={18} /> Travel
      </button>
      <h1 className="h1 h1-sm">Add a booking</h1>
      <SegmentForm
        initial={{ type: 'stay', start: { date: '' }, who: me ? [me] : [] }}
        submitLabel="Save"
        onSubmit={(seg) => {
          const r = store.saveManualSegment(seg);
          if (r.ok) {
            store.toast('Saved');
            const latest = [...store.state.segments].sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
            navigate(latest ? `/trips/${latest.tripId}/${latest.id}` : '/trips', { replace: true, transition: 'forward' });
          }
          return r.problems;
        }}
      />
    </div>
  );
}

export function EditSegmentScreen({ tripId, segmentId }: { tripId: string; segmentId: string }) {
  const store = getStore();
  const segments = useSegments();
  const people = useAppState((s) => s.people);
  const seg = segments.find((s) => s.id === segmentId);
  const initial = useMemo<Partial<ExtractedSegment> | undefined>(
    () =>
      seg && {
        type: seg.type,
        title: seg.title,
        start: seg.start,
        end: seg.end,
        from: seg.from,
        to: seg.to,
        place: seg.place,
        confirmation: seg.confirmation,
        provider: seg.provider,
        number: seg.number,
        travelers: seg.travelers,
        seats: seg.seats,
        fare: seg.fare,
        price: seg.price,
        bags: seg.bags,
        who: seg.who ?? whoIsGoing(seg, people).people,
        notes: seg.notes,
        details: seg.details,
      },
    [seg, people],
  );
  if (!seg) return <div className="screen"><div className="empty">This booking no longer exists.</div></div>;
  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back(`/trips/${tripId}/${segmentId}`)}>
        <Icon name="chevron-left" size={18} /> Back
      </button>
      <h1 className="h1 h1-sm">Edit booking</h1>
      <SegmentForm
        initial={initial}
        submitLabel="Save changes"
        onSubmit={(s) => {
          const r = store.saveManualSegment(s, segmentId);
          if (r.ok) {
            store.toast('Saved');
            navigate(`/trips/${tripId}/${segmentId}`, { replace: true, transition: 'back' });
          }
          return r.problems;
        }}
      />
    </div>
  );
}

export function AttentionListScreen() {
  const emails = useAppState((s) => s.emails);
  const people = useAppState((s) => s.people);
  const failed = emails.filter((e) => e.status === 'failed').sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/trips')}>
        <Icon name="chevron-left" size={18} /> Travel
      </button>
      <div className="stack" style={{ gap: 4 }}>
        <h1 className="h1 h1-sm">Needs attention</h1>
        <span className="muted" style={{ fontSize: 14 }}>
          {failed.length === 0 ? 'All forwarded emails were read.' : `${failed.length} email${failed.length === 1 ? '' : 's'} couldn't be read completely`}
        </span>
      </div>
      {failed.map((e) => (
        <Link key={e.id} to={`/trips/attention/${e.id}`} className="card card-link">
          <span className="grow stack" style={{ gap: 4 }}>
            <span style={{ fontWeight: 600 }}>{e.subject}</span>
            <span className="small muted">
              Forwarded by {people.find((p) => p.id === e.forwardedBy)?.initial ?? '?'} · {formatShortDate(e.receivedAt.slice(0, 10))}
            </span>
            <span className="badge badge-orange" style={{ alignSelf: 'flex-start' }}>
              {(e.problems ?? ['Needs a look'])[0]}
            </span>
          </span>
          <Icon name="chevron-right" size={18} />
        </Link>
      ))}
    </div>
  );
}

export function AttentionScreen({ emailId }: { emailId: string }) {
  const store = getStore();
  const emails = useAppState((s) => s.emails);
  const people = useAppState((s) => s.people);
  const email = emails.find((e) => e.id === emailId);
  if (!email) return <div className="screen"><div className="empty">This email was already handled.</div></div>;
  const by = people.find((p) => p.id === email.forwardedBy);
  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/trips/attention')}>
        <Icon name="chevron-left" size={18} /> Needs attention
      </button>
      <div className="stack" style={{ gap: 4 }}>
        <h1 className="h1 h1-sm">{email.subject}</h1>
        <span className="small muted">
          Forwarded by {by?.name ?? 'unknown'} · {formatShortDate(email.receivedAt.slice(0, 10))}
        </span>
      </div>
      <SegmentForm
        initial={email.draft as Partial<ExtractedSegment> | undefined}
        problems={email.problems}
        submitLabel="Save booking"
        onSubmit={(seg) => {
          const r = store.resolveEmail(email.id, seg);
          if (r.ok) {
            store.toast('Saved');
            navigate('/trips/attention', { replace: true, transition: 'back' });
          }
          return r.problems;
        }}
        secondary={
          <button
            type="button"
            className="btn"
            onClick={() => {
              store.ignoreEmail(email.id);
              navigate('/trips/attention', { replace: true, transition: 'back' });
            }}
          >
            Ignore
          </button>
        }
      />
      {email.bodyPreview && (
        <details className="card">
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>The original email</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 13, color: 'var(--muted)', margin: '12px 0 0' }}>{email.bodyPreview}</pre>
        </details>
      )}
    </div>
  );
}
